import { fetchMyRouterUsage, type RouterUsage } from '@/api/enterpriseAdmin';
import { TOP_BAR_CONTROL_STATE_CLASS } from '@/components/TopBar/controlStyles';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';
import { Coins } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

const REFRESH_INTERVAL_MS = 60_000;

const numberFormat = new Intl.NumberFormat('zh-CN');

function formatPoints(value: number | null | undefined): string {
  return Number.isFinite(value) ? numberFormat.format(Number(value)) : '--';
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5 text-sm">
      <span className="text-ds-ink-muted-default">{label}</span>
      <span className="tabular-nums font-medium text-ds-ink-default-default">
        {value}
      </span>
    </div>
  );
}

export function PointsIndicator() {
  const token = useAuthStore((state) => state.token);
  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState<RouterUsage | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const next = await fetchMyRouterUsage();
      setUsage(next);
      setUnavailable(false);
    } catch {
      setUnavailable(true);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setUsage(null);
      return;
    }
    void refresh();
    const timer = window.setInterval(() => void refresh(), REFRESH_INTERVAL_MS);
    const handleFocus = () => void refresh();
    window.addEventListener('focus', handleFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refresh, token]);

  const displayPoints = unavailable
    ? '--'
    : formatPoints(usage?.points_remaining);
  const label = `积分 ${displayPoints}`;
  const estimatedCost = useMemo(() => {
    const numeric = Number(usage?.estimated_cost_cny);
    return Number.isFinite(numeric) ? `¥${numeric.toFixed(3)}` : '¥0.000';
  }, [usage?.estimated_cost_cny]);

  if (!token) return null;

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) void refresh();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className={cn(
            'no-drag flex h-7 items-center gap-1 rounded-full px-2 text-xs font-medium text-ds-ink-default-default',
            TOP_BAR_CONTROL_STATE_CLASS
          )}
        >
          <Coins className="h-3.5 w-3.5" aria-hidden />
          <span className="tabular-nums">{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-72 p-3">
        <div className="mb-2 text-sm font-semibold text-ds-ink-default-default">
          企业模型积分
        </div>
        <DetailRow label="当前周期" value={usage?.period_id || '--'} />
        <DetailRow
          label="月度额度"
          value={formatPoints(usage?.points_limit)}
        />
        <DetailRow
          label="临时调整"
          value={formatPoints(usage?.points_adjustment)}
        />
        <DetailRow label="已用积分" value={formatPoints(usage?.points_used)} />
        <DetailRow
          label="预占积分"
          value={formatPoints(usage?.points_reserved)}
        />
        <DetailRow label="剩余积分" value={displayPoints} />
        <div className="my-2 border-t border-ds-border-default" />
        <DetailRow label="Token 用量" value={formatPoints(usage?.total_tokens)} />
        <DetailRow label="折算成本" value={estimatedCost} />
        <div className="mt-2 text-xs text-ds-ink-muted-default">
          1000 积分 = 1 元
        </div>
      </PopoverContent>
    </Popover>
  );
}
